/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react-hooks/exhaustive-deps */
import { RoleFunction } from '@eagle/common';
import { Dashboard } from '@eagle/core-data-types';
import { FindItemsDeferredResult, getListResultDescription, Icon, ListPage, ListPageQuery, Pagination, ResultsDetailCard, SearchResultsTableView, trackEvent, Undefinable, useAuthenticated, useBoolFlag, useCustomRoutes, useHasAuthorization } from '@eagle/react-common';
import { Button, Card, Stack, styled, TableBody, TableCell, TableRow, Typography } from '@ethings/ui-toolkit';
import { TableRow as MUITableRow } from '@mui/material';
import Axios from 'axios';
import { FC, useCallback, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHref, useNavigate } from 'react-router-dom';
import { DASHBOARD_DETAIL_FLAG } from '../dashboard-detail/dashboard-detail';
import { SPARE_FLEET_CAPACITY_FLAG } from '../reports-spare-fleet-capacity/reports-spare-fleet-capacity';

const USER_ROLE = [RoleFunction.USER] as const;
const DATA_VIEWER_ROLE = [RoleFunction.DATA_VIEWER] as const;

type ReportEntry = { kind: 'dashboard'; item: Dashboard } | { kind: 'static' };

interface StyledTableBodyProps {
  isLoading: boolean;
}

const StyledTableBody = styled(TableBody, {
  shouldForwardProp: (prop) => prop !== 'isLoading',
})<StyledTableBodyProps>(({ isLoading }) => ({
  filter: isLoading ? 'blur(1px)' : '',
  opacity: isLoading ? 0.66 : 1,
}));

const StyledTableRow = styled(TableRow)() as typeof MUITableRow;

export const ReportsList: FC = () => {
  const { axios, restClient } = useAuthenticated();
  const { t } = useTranslation(['common', 'track']);
  const { reports } = useCustomRoutes();
  const navigate = useNavigate();
  const href = useHref(`/${reports}`);

  const { hasAuthorization } = useHasAuthorization();
  const SYSTEM_ADMINISTRATOR_ROLE = [RoleFunction.SYSTEM_ADMINISTRATOR] as const;
  const hasManagePermissions = hasAuthorization(SYSTEM_ADMINISTRATOR_ROLE) && useBoolFlag(DASHBOARD_DETAIL_FLAG);
  const showSpareFleetCapacity = hasAuthorization(USER_ROLE) && hasAuthorization(DATA_VIEWER_ROLE) && useBoolFlag(SPARE_FLEET_CAPACITY_FLAG);

  const spareFleetCapacityLabel = t('track:page.reports.spare-fleet-capacity.title');

  const buildReportEntries = (items: Dashboard[]): ReportEntry[] => {
    const entries: ReportEntry[] = items.map((item) => ({ kind: 'dashboard', item }));
    if (!showSpareFleetCapacity) return entries;

    const insertAt = items.findIndex((item) => spareFleetCapacityLabel.localeCompare(item.display) <= 0);
    entries.splice(insertAt === -1 ? entries.length : insertAt, 0, { kind: 'static' });
    return entries;
  };

  const navigateToReports = (item: Dashboard): void => {
    navigate(`/${reports}/${encodeURIComponent(item._id)}`);
  };

  const findReports = useCallback(({ pagination, search }: ListPageQuery): FindItemsDeferredResult<Dashboard> => {
    const cancelToken = Axios.CancelToken.source();

    return {
      cancel: () => cancelToken.cancel(),
      promise: restClient.my.dashboards.getAllV2({
        ...pagination,
        ...(search ? { search } : {}),
        sort: !search ? JSON.stringify({ display: 'asc' }) : undefined,
        filter: { type: 'report' },
      }, { cancelToken: cancelToken.token }).then((response) => {
        const matchCount = response.count ?? 0;
        const resultDescription = getListResultDescription({ count: matchCount, entityKey: 'common:terms.report', search, t });

        if (search) {
          trackEvent('keyword_search', 'returned_results', 'reports_list', { 'results_type': 'reports', 'results_count': matchCount, 'search_term': search });
        }

        return {
          result: {
            results: response.items,
            itemCount: matchCount,
          },
          resultDescription,
        };
      }),
    };
  }, [axios]);

  const renderDesktopContent = (isLoading: boolean, items: Dashboard[]): JSX.Element => <>
    <StyledTableBody  component="div" isLoading={isLoading}>
      {buildReportEntries(items).map((entry) => entry.kind === 'static'
        ? <StyledTableRow
          key="spare-fleet-capacity"
          component={Link}
          hover
          to={`/${reports}/spare-fleet-capacity`}
          sx={{ cursor: 'pointer', height: 60, textDecoration: 'none' }}
        >
          <TableCell component="div">
            <Typography>{spareFleetCapacityLabel}</Typography>
          </TableCell>
        </StyledTableRow>
        : <StyledTableRow
          key={entry.item._id}
          component={Link}
          hover
          to={`/${reports}/${entry.item._id}`}
          sx={{ cursor: 'pointer', height: 60, textDecoration: 'none' }}
        >
          <TableCell component="div">
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography>{entry.item.display}</Typography>
              {hasManagePermissions
                && <Button
                  onClick={(e) => {
                    navigate(`/${reports}/manage/${entry.item._id}`);
                    e.preventDefault();
                  }}
                  variant="text"
                >
                  {t('track:page.dashboards.manage.action')}
                </Button>
              }
            </Stack>
          </TableCell>
        </StyledTableRow>
      )}
    </StyledTableBody>
  </>;

  const renderMobileContent = (_displayOnCard: boolean, isLoading: boolean, items: Dashboard[]): JSX.Element =>
    <Stack spacing={1} width={1}>
      {buildReportEntries(items).map((entry) => entry.kind === 'static'
        ? <Card key="spare-fleet-capacity" style={{ filter: isLoading ? 'blur(1px)' : '', overflow: 'visible', width: '100%' }}>
          <ResultsDetailCard
            content={<Typography>{spareFleetCapacityLabel}</Typography>}
            href={`${href}/spare-fleet-capacity`}
            sx={{ py: 1, px: 2 }}
          />
        </Card>
        : <Card key={entry.item._id} style={{ filter: isLoading ? 'blur(1px)' : '', overflow: 'visible', width: '100%' }}>
          <ResultsDetailCard
            content={<Typography>{entry.item.display}</Typography>}
            href={`${href}/${entry.item._id}`}
            sx={{ py: 1, px: 2 }}
          />
        </Card>
      )}
    </Stack>;

  const renderTableContent = (
    isLoading: boolean,
    items: Undefinable<Dashboard[]>,
    matchCount: number,
    pagination: Pagination,
    setPagination: (value: Pagination) => void,
    text: Undefinable<string>,
    error?: Error,
  ): JSX.Element => (
    <SearchResultsTableView<Dashboard>
      data-testid='reports-list-results'
      isLoading={isLoading}
      items={items}
      matchCount={matchCount}
      noResultsInstructions={t('track:page.reports-list.no-reports.hint')}
      pagination={pagination}
      renderDesktopContent={renderDesktopContent}
      renderMobileContent={renderMobileContent}
      setPagination={setPagination}
      text={text}
      error={error}
    />
  );

  return (
    <ListPage<Dashboard>
      id='report'
      data-testid='reports-list'
      actions={[]}
      filterFields={[]}
      icon={<Icon icon="ReportsIcon" />}
      navigateToEntity={navigateToReports}
      onQueryChanged={findReports}
      renderTableContent={renderTableContent}
      searchRemoved
      showFilterButton={false}
      storageKey="reports"
      title={t('track:page.reports.title')}
    />
  );
};
